import React from "react";
import styled from "@emotion/styled";
import { lighten, spacing, SpacingProps } from "@mui/system";
import { useTranslation } from "react-i18next";
import {
  useTheme,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip as MuiChip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Style,
  Group,
  Event,
  Euro,
  Public,
  Subject,
  PanTool,
  Store,
  MenuBook,
  Class,
  Money,
} from "@mui/icons-material";
import { DollarSign } from "react-feather";

import { IContract } from "../../../../../../types/contract";
import { ContractExtractionResult } from "../../../../../../types/aiOutput";
import { DialogMode } from "../../../../../../types/dialogmode";
import { IDocument } from "../../../../../../types/document";

import ContractCategory from "../../ContractCategory";
import ContractContactPersons from "../../ContractContactPersons";
import ContractDates, { ContractDatesCard } from "../../ContractDates";
import ContractFinancials, { FinancialsCard } from "../../ContractFinancials";
import ContractJurisdiction from "../../ContractJurisdiction";
import ContractSubject from "../../ContractSubject";
import ContractNoticePeriod, {
  NoticePeriodCard,
} from "../../ContractNoticePeriod";
import ContractParties from "../../ContractParties";
import ContractSummary from "../../ContractSummary";
import ContractType from "../../ContractType";

interface ChipProps extends SpacingProps {
  component?: React.ElementType;
  href?: string;
  icon?: JSX.Element | null;
}
const Chip = styled(MuiChip)<ChipProps>(spacing);

interface BasicDetailsTabProps {
  contract: IContract;
  mode: DialogMode;
  aiOutputs: ContractExtractionResult[];
  documents: IDocument[];
}

const BasicDetailsTab: React.FC<BasicDetailsTabProps> = ({
  contract,
  mode,
  aiOutputs,
  documents,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const clearMarkdown = (text: string) => {
    if (!text) return "";

    // remove all html tags
    const regex = /(<([^>]+)>)/gi;
    const cleanText = text.replace(regex, "");

    // remove all new lines
    const cleanText2 = cleanText.replace(/(\r\n|\n|\r)/gm, "");

    // remove all markdown tags, like ##
    const regex2 = /(\*\*|__|~~|`|>|#)/g;
    const cleanText3 = cleanText2.replace(regex2, "");

    return cleanText3;
  };

  return (
    <Stack>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-category"
          id="panel-category-header"
        >
          <ListItem disablePadding disableGutters>
            <ListItemIcon
              sx={{
                color: contract.contractCategory
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main,
              }}
            >
              <Style />
            </ListItemIcon>
            <ListItemText
              primary={t("Category")}
              secondary={
                contract?.contractCategory
                  ? t(contract.contractCategory.title)
                  : ""
              }
            />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <ContractCategory
            contract={contract}
            mode={mode}
            aiOutputs={aiOutputs}
            hideTitle
            documents={documents}
          />
        </AccordionDetails>
      </Accordion>

      {(contract?.buyer?.contactPersons?.length > 0 ||
        contract?.seller?.contactPersons?.length > 0) && (
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-contactpersons"
            id="panel-contactpersons-header"
          >
            <ListItem disablePadding disableGutters>
              <ListItemIcon
                sx={{
                  color:
                    contract.buyer?.contactPersons?.length > 0 ||
                    contract.seller?.contactPersons?.length > 0
                      ? theme.palette.primary.main
                      : theme.palette.secondary.main,
                }}
              >
                <Group />
              </ListItemIcon>
              <ListItemText
                primary={t("Contact persons")}
                secondary={[
                  ...contract.seller?.contactPersons,
                  ...contract.buyer?.contactPersons,
                ]
                  .map((cp) => cp.name)
                  .join(", ")}
              />
            </ListItem>
          </AccordionSummary>
          <AccordionDetails>
            <ContractContactPersons
              mode={mode}
              contract={contract}
              aiOutputs={aiOutputs}
              hideTitle
              documents={documents}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-enddate"
          id="panel-enddate-header"
        >
          <ListItem
            disablePadding
            disableGutters
            secondaryAction={
              contract.dates?.logicalCheck?.logic_check?.is_logical ===
                false && (
                <Chip
                  label={t("Risk found")} // need to classify low, medium, high
                  color="warning"
                  size="small"
                  sx={{
                    textTransform: "uppercase",
                    // use warning.light but make it lighter
                    backgroundColor: lighten(theme.palette.warning.light, 0.8),
                    color: "warning.main",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: lighten(
                        theme.palette.warning.light,
                        0.7
                      ),
                      color: "warning.main",
                    },
                  }}
                  variant="filled"
                  onClick={() => console.log("Risk chip clicked")}
                />
              )
            }
          >
            <ListItemIcon
              sx={{
                color: contract.dates?.endDate
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main,
              }}
            >
              <Event />
            </ListItemIcon>
            <ListItemText
              primary={t("End date")}
              secondary={
                contract.dates ? (
                  <ContractDatesCard {...contract.dates} viewMode="compact" />
                ) : aiOutputs?.find(
                    (a) => a.metadata?.structuralData?.dates
                  ) ? (
                  <ContractDatesCard
                    {...aiOutputs?.find(
                      (a) => a.metadata?.structuralData?.dates
                    )?.metadata?.structuralData?.dates}
                    viewMode="compact"
                  />
                ) : (
                  <ContractDatesCard {...contract} viewMode="compact" />
                )
              }
            />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <ContractDates
            contract={contract}
            documents={documents}
            mode={mode}
            aiOutputs={aiOutputs}
            hideTitle
          />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-financials"
          id="panel-financials-header"
        >
          <ListItem
            disablePadding
            disableGutters
            secondaryAction={
              contract.budget?.logicalCheck?.logic_check?.is_logical ===
                false && (
                <Chip
                  label={t("Risk found")} // need to classify low, medium, high
                  color="warning"
                  size="small"
                  sx={{
                    textTransform: "uppercase",
                    // use warning.light but make it lighter
                    backgroundColor: lighten(theme.palette.warning.light, 0.8),
                    color: "warning.main",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: lighten(
                        theme.palette.warning.light,
                        0.7
                      ),
                      color: "warning.main",
                    },
                  }}
                  variant="filled"
                  onClick={() => console.log("Risk chip clicked")}
                />
              )
            }
          >
            <ListItemIcon
              sx={{
                color: contract.budget?.totalValue
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main,
              }}
            >
              {contract?.budget?.currency === "EUR" ? (
                <Euro />
              ) : contract?.budget?.currency === "USD" ? (
                <DollarSign />
              ) : (
                <Money />
              )}
            </ListItemIcon>
            <ListItemText
              primary={t("Financials")}
              secondary={
                contract?.budget ? (
                  <FinancialsCard {...contract.budget} viewMode="compact" />
                ) : aiOutputs?.find(
                    (a) => a.metadata?.structuralData?.financials
                  ) ? (
                  <FinancialsCard
                    {...aiOutputs?.find(
                      (a) => a.metadata?.structuralData?.financials
                    )?.metadata?.structuralData?.financials}
                    viewMode="compact"
                  />
                ) : (
                  ""
                )
              }
            />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <ContractFinancials
            mode={mode}
            contract={contract}
            documents={documents}
            aiOutputs={aiOutputs}
            hideTitle
          />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-jurisdiction"
          id="panel-jurisdiction-header"
        >
          <ListItem disablePadding disableGutters>
            <ListItemIcon
              sx={{
                color: contract.countryOfJurisdiction
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main,
              }}
            >
              <Public />
            </ListItemIcon>
            <ListItemText
              primary={t("Country of jurisdiction")}
              secondary={
                contract?.countryOfJurisdiction ||
                aiOutputs?.find(
                  (a) => a.metadata?.structuralData?.countryOfJurisdiction
                )?.metadata?.structuralData?.countryOfJurisdiction
              }
            />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <ContractJurisdiction
            contract={contract}
            mode={mode}
            aiOutputs={aiOutputs}
            hideTitle
            documents={documents}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-subject"
          id="panel-subject-header"
        >
          <ListItem disablePadding disableGutters>
            <ListItemIcon
              sx={{
                color: contract.subject
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main,
              }}
            >
              <Subject />
            </ListItemIcon>
            <ListItemText
              primary={t("Subject")}
              secondary={
                contract.subject ||
                aiOutputs?.find((a) => a.metadata?.structuralData?.subject)
                  ?.metadata?.structuralData?.subject
              }
            />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <ContractSubject
            contract={contract}
            mode={mode}
            aiOutputs={aiOutputs}
            hideTitle
            documents={documents}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-noticeperiod"
          id="panel-noticeperiod-header"
        >
          <ListItem
            disablePadding
            disableGutters
            secondaryAction={
              contract.noticePeriod?.logicalCheck?.logic_check?.is_logical ===
                false && (
                <Chip
                  label={t("Risk found")} // need to classify low, medium, high
                  color="warning"
                  size="small"
                  sx={{
                    textTransform: "uppercase",
                    // use warning.light but make it lighter
                    backgroundColor: lighten(theme.palette.warning.light, 0.8),
                    color: "warning.main",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: lighten(
                        theme.palette.warning.light,
                        0.7
                      ),
                      color: "warning.main",
                    },
                  }}
                  variant="filled"
                  onClick={() => console.log("Risk chip clicked")}
                />
              )
            }
          >
            <ListItemIcon
              sx={{
                color: contract.noticePeriod?.value
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main,
              }}
            >
              <PanTool />
            </ListItemIcon>
            <ListItemText
              primary={t("Notice period")}
              secondary={
                contract.noticePeriod ? (
                  <NoticePeriodCard
                    {...contract.noticePeriod}
                    viewMode="compact"
                  />
                ) : aiOutputs?.find(
                    (a) => a.metadata?.structuralData?.noticePeriod
                  ) ? (
                  <NoticePeriodCard
                    {...aiOutputs?.find(
                      (a) => a.metadata?.structuralData?.noticePeriod
                    )?.metadata?.structuralData?.noticePeriod}
                    viewMode="compact"
                  />
                ) : (
                  ""
                )
              }
            />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <ContractNoticePeriod
            contract={contract}
            mode={mode}
            aiOutputs={aiOutputs}
            hideTitle
            documents={documents}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-parties"
          id="panel-parties-header"
        >
          <ListItem disablePadding disableGutters>
            <ListItemIcon
              sx={{
                color:
                  contract.buyer || contract.seller
                    ? theme.palette.primary.main
                    : theme.palette.secondary.main,
              }}
            >
              <Store />
            </ListItemIcon>
            <ListItemText
              primary={t("Parties")}
              secondary={
                contract.buyer || contract.seller ? (
                  <>
                    {[contract.buyer?.name, contract.seller?.name]
                      .filter((s) => s)
                      .join(", ")}
                  </>
                ) : (
                  `${[
                    aiOutputs?.find((a) => a.metadata?.structuralData?.buyer)
                      ?.metadata.structuralData?.buyer?.name,
                    aiOutputs?.find((a) => a.metadata?.structuralData?.seller)
                      ?.metadata.structuralData?.seller?.name,
                  ]
                    .filter((s) => s)
                    .join(", ")}`
                )
              }
            />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <ContractParties
            contract={contract}
            mode={mode}
            aiOutputs={aiOutputs}
            hideTitle
            documents={documents}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-summary"
          id="panel-summary-header"
        >
          <ListItem disablePadding disableGutters>
            <ListItemIcon
              sx={{
                color: contract.summary
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main,
              }}
            >
              <MenuBook />
            </ListItemIcon>
            <ListItemText
              primary={t("Summary")}
              secondary={
                contract.summary
                  ? clearMarkdown(contract.summary?.substring(0, 100) + "...")
                  : ""
              }
            />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <ContractSummary
            contract={contract}
            mode={mode}
            aiOutputs={aiOutputs}
            hideTitle
            documents={documents}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-type"
          id="panel-type-header"
        >
          <ListItem disablePadding disableGutters>
            <ListItemIcon
              sx={{
                color: contract.type
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main,
              }}
            >
              <Class />
            </ListItemIcon>
            <ListItemText primary={t("Type")} secondary={t(contract.type!)} />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <ContractType
            contract={contract}
            mode={mode}
            aiOutputs={aiOutputs}
            hideTitle
            documents={documents}
          />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default BasicDetailsTab;
